import { TranslationNamespace } from '../resource/translationNamespace.js';
import { getI18nInstance } from '../resource/i18n.js';
import { getLanguageTranslationKey } from '../resource/getCurrentLanguage.js';
import {
  localeConvertor,
  DEFAULT_LANGUAGE,
  PREFERRED_LOCALES,
} from '../resource/i18nCommon.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { SUPPORTED_LANGUAGES } from '../RemoteConfigKeys.js';
import { CategoryI18nKey } from '../resource/categoryConstants.js';

/**
 * Generate JSON-LD for category collection pages
 * Supports two modes:
 * 1. Category All: Multiple feeds with 2-level breadcrumb
 * 2. Category ListType: Single feed with 3-level breadcrumb
 *
 * @param {Object} params
 * @param {string} params.origin - URL origin (default: 'https://swag.live')
 * @param {string} params.categoryId - Category ID
 * @param {string} [params.listType] - Optional list type (flix/short/image). If provided, generates Category ListType mode
 * @param {Object} params.state - Redux state (Immutable.js)
 * @returns {Object} JSON-LD object
 */
export const generateCollectionPageJsonLd = async ({
  origin = 'https://swag.live',
  categoryId,
  listType = null,
  state,
}) => {
  const { i18n } = getI18nInstance();
  const { t } = i18n;
  // if not pass listType, it is Category All
  const isCategoryListType = !!listType;

  const supportedLanguages =
    getRemoteConfigData(state, SUPPORTED_LANGUAGES) || PREFERRED_LOCALES;
  const alternateLanguages = supportedLanguages.filter(
    lang => DEFAULT_LANGUAGE !== lang
  );

  const loadLanguagePromises = alternateLanguages.map(async lang => {
    if (!i18n.hasResourceBundle(lang, TranslationNamespace.SEO)) {
      await i18n.reloadResources(lang, TranslationNamespace.SEO);
    }
  });

  await Promise.all(loadLanguagePromises);

  const localeToBCP47 = lang =>
    localeConvertor({ locale: lang, isBCP47: true });

  const getCategoryNameByLang = lang =>
    t(`category_${categoryId}`, {
      ns: TranslationNamespace.FEED,
      lng: lang,
    });

  const getCategoryAllTitle = lang =>
    t('seo_category_all_title', {
      ns: TranslationNamespace.SEO,
      lng: lang,
      category: getCategoryNameByLang(lang),
    });

  const getCategoryPageTitle = lang => {
    if (!isCategoryListType) {
      return getCategoryAllTitle(lang);
    }
    const categoryListType = CategoryI18nKey[listType];
    return t(`seo_category_${categoryListType}_title`, {
      ns: TranslationNamespace.SEO,
      lng: lang,
      category: getCategoryNameByLang(lang),
    });
  };

  const getHomeTitle = lang =>
    t('seo-home-title', {
      ns: TranslationNamespace.SEO,
      lng: lang,
    });

  const buildAlternateValues = translateFn =>
    alternateLanguages.map(lang => ({
      '@value': translateFn(lang),
      '@language': localeToBCP47(lang),
    }));

  const pageTitle = {
    '@value': getCategoryPageTitle(DEFAULT_LANGUAGE),
    '@language': localeToBCP47(DEFAULT_LANGUAGE),
  };

  const allCategoryTitle = {
    '@value': getCategoryAllTitle(DEFAULT_LANGUAGE),
    '@language': localeToBCP47(DEFAULT_LANGUAGE),
  };

  const homeTitle = {
    '@value': getHomeTitle(DEFAULT_LANGUAGE),
    '@language': localeToBCP47(DEFAULT_LANGUAGE),
  };

  // Build @graph array
  const graph = [];

  // 1. CollectionPage
  graph.push({
    '@type': 'CollectionPage',
    '@id': isCategoryListType
      ? `${origin}/category/${categoryId}/${listType}#webpage`
      : `${origin}/category/${categoryId}#webpage`,
    url: isCategoryListType
      ? `${origin}/category/${categoryId}/${listType}`
      : `${origin}/category/${categoryId}`,
    inLanguage: supportedLanguages.map(lang => ({
      '@type': 'Language',
      name: t(getLanguageTranslationKey({ language: lang }), {
        ns: TranslationNamespace.SEO,
      }),
      alternateName: localeToBCP47(lang),
    })),
    name: pageTitle,
    alternateName: buildAlternateValues(getCategoryPageTitle),
    breadcrumb: {
      '@id': isCategoryListType
        ? `${origin}/category/${categoryId}/${listType}#breadcrumb`
        : `${origin}/category/${categoryId}#breadcrumb`,
    },
    mainEntity: {
      '@id': isCategoryListType
        ? `${origin}/category/${categoryId}/${listType}#feed`
        : `${origin}/category/${categoryId}#feed`,
    },
  });

  // 2. BreadcrumbList
  graph.push({
    '@type': 'BreadcrumbList',
    '@id': isCategoryListType
      ? `${origin}/category/${categoryId}/${listType}#breadcrumb`
      : `${origin}/category/${categoryId}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: homeTitle,
        alternateName: buildAlternateValues(getHomeTitle),
        item: {
          '@id': `${origin}/#website`,
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: allCategoryTitle,
        alternateName: buildAlternateValues(getCategoryAllTitle),
        item: {
          '@id': `${origin}/category/${categoryId}#webpage`,
        },
      },
      ...(isCategoryListType
        ? [
            {
              '@type': 'ListItem',
              position: 3,
              name: pageTitle,
              alternateName: buildAlternateValues(getCategoryPageTitle),
              item: {
                '@id': `${origin}/category/${categoryId}/${listType}#webpage`,
              },
            },
          ]
        : []),
    ],
  });

  // 3 Map ItemList elements
  graph.push({
    '@type': 'ItemList',
    '@id': isCategoryListType
      ? `${origin}/category/${categoryId}/${listType}#feed`
      : `${origin}/category/${categoryId}#feed`,
    url: isCategoryListType
      ? `${origin}/category/${categoryId}/${listType}`
      : `${origin}/category/${categoryId}`,
  });

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
};

export default generateCollectionPageJsonLd;
